import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Stack,
  TextField,
  Button,
  InputAdornment,
  Typography,
  Pagination,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Rating,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Chip,
  useTheme,
  alpha,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react";
import { MoreVert } from "@mui/icons-material";

import { Strings, RequestStatus } from "@app/constants";
import { getTestimonials } from "../slice";
import { selectTestimonialsState } from "../selector";
import { ITestimonial } from "../types";
import { AddEditTestimonialModal } from "./add-edit-testimonial-modal";
import { TestimonialDetailModal } from "./testimonial-detail-modal";
import { DeleteTestimonialPopup } from "./delete-testimonial-popup";

const TestimonialsPage = () => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const testimonialsState = useSelector(selectTestimonialsState);
  const isLoading = testimonialsState?.status === RequestStatus.RUNNING;

  // Local states
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedRatingFilter, setSelectedRatingFilter] =
    useState<string>("All");
  const [selectedVerifiedFilter, setSelectedVerifiedFilter] =
    useState<string>("All");
  const [page, setPage] = useState<number>(1);
  const rowsPerPage = 10;

  // Action Menu State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<ITestimonial | null>(null);

  // Modals state
  const [openAddEditModal, setOpenAddEditModal] = useState<boolean>(false);
  const [selectedItemToEdit, setSelectedItemToEdit] =
    useState<ITestimonial | null>(null);

  const [openDetailModal, setOpenDetailModal] = useState<boolean>(false);
  const [selectedItemToView, setSelectedItemToView] =
    useState<ITestimonial | null>(null);

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [selectedItemToDelete, setSelectedItemToDelete] =
    useState<ITestimonial | null>(null);

  useEffect(() => {
    dispatch(getTestimonials());
  }, [dispatch]);

  // Extract raw list
  const rawList: ITestimonial[] = useMemo(() => {
    const d = testimonialsState?.data;
    if (Array.isArray(d)) return d;
    if (d?.testimonials && Array.isArray(d.testimonials)) return d.testimonials;
    if (d?.data && Array.isArray(d.data)) return d.data;
    return [];
  }, [testimonialsState?.data]);

  // Filtered list
  const filteredList = useMemo(() => {
    return rawList.filter((item: ITestimonial) => {
      const q = searchQuery.toLowerCase().trim();
      const reviewerName = (
        item.reviewerName ||
        item.name ||
        item.author ||
        ""
      ).toLowerCase();
      const commentText = (
        item.comment ||
        item.review ||
        item.message ||
        ""
      ).toLowerCase();
      const tagText = (item.tag || "").toLowerCase();

      const matchesSearch =
        !q ||
        reviewerName.includes(q) ||
        commentText.includes(q) ||
        tagText.includes(q);

      const ratingVal = Number(item.rating ?? 5);
      const matchesRating =
        selectedRatingFilter === "All" ||
        Math.floor(ratingVal) === Number(selectedRatingFilter);

      const isVerified = Boolean(item.verifiedBuyer);
      const matchesVerified =
        selectedVerifiedFilter === "All" ||
        (selectedVerifiedFilter === "verified" && isVerified) ||
        (selectedVerifiedFilter === "unverified" && !isVerified);

      return matchesSearch && matchesRating && matchesVerified;
    });
  }, [rawList, searchQuery, selectedRatingFilter, selectedVerifiedFilter]);

  const total = filteredList.length;
  const totalPages = Math.ceil(total / rowsPerPage) || 1;

  // Paginated list
  const paginatedList = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredList.slice(start, start + rowsPerPage);
  }, [filteredList, page, rowsPerPage]);

  // Action Menu handlers
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    item: ITestimonial
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedRow(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Modals Open handlers
  const handleOpenAdd = () => {
    setSelectedItemToEdit(null);
    setOpenAddEditModal(true);
  };

  const handleOpenEdit = (item: ITestimonial) => {
    setSelectedItemToEdit(item);
    setOpenAddEditModal(true);
  };

  const handleOpenView = (item: ITestimonial) => {
    setSelectedItemToView(item);
    setOpenDetailModal(true);
  };

  const handleOpenDelete = (item: ITestimonial) => {
    setSelectedItemToDelete(item);
    setOpenDeleteModal(true);
  };

  const fontFamily = Strings.fontLanguage.workSans || "'Work Sans', sans-serif";

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: "#fff",
        borderRadius: 1,
        minHeight: { xs: "auto", md: "calc(100vh - 64px)" },
        boxSizing: "border-box",
      }}
    >
      {/* ----------------- Top Header Controls ----------------- */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <TextField
          fullWidth
          placeholder="Search by reviewer name, comment, or tag..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon icon="mdi:magnify" width={20} />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, maxWidth: { sm: 380 } }}
        />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems="center"
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <TextField
            select
            size="small"
            label="Rating"
            value={selectedRatingFilter}
            onChange={(e) => {
              setSelectedRatingFilter(e.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 130, width: { xs: "100%", sm: "auto" } }}
          >
            <MenuItem value="All">All Ratings</MenuItem>
            <MenuItem value="5">5 Stars</MenuItem>
            <MenuItem value="4">4 Stars</MenuItem>
            <MenuItem value="3">3 Stars</MenuItem>
            <MenuItem value="2">2 Stars</MenuItem>
            <MenuItem value="1">1 Star</MenuItem>
          </TextField>

          <TextField
            select
            size="small"
            label="Buyer Status"
            value={selectedVerifiedFilter}
            onChange={(e) => {
              setSelectedVerifiedFilter(e.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 140, width: { xs: "100%", sm: "auto" } }}
          >
            <MenuItem value="All">All Status</MenuItem>
            <MenuItem value="verified">Verified Buyer</MenuItem>
            <MenuItem value="unverified">Unverified</MenuItem>
          </TextField>

          <Button
            variant="contained"
            onClick={handleOpenAdd}
            sx={{
              borderRadius: 1,
              color: theme.palette.common.white,
              backgroundColor: theme.palette.primary.main,
              height: 40,
              minWidth: 140,
              width: { xs: "100%", sm: "auto" },
              textTransform: "none",
              fontWeight: 600,
              fontFamily,
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            Add Review
          </Button>
        </Stack>
      </Stack>

      {/* ----------------- Table Container ----------------- */}
      <TableContainer
        component={Paper}
        sx={{
          pb: 3,
          boxShadow: "none",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {[
                { label: "Reviewer", align: "left" },
                { label: "Rating", align: "center" },
                { label: "Comment", align: "left" },
                { label: "Buyer Status", align: "center" },
                { label: "Tag", align: "center" },
                { label: "Date", align: "center" },
                { label: "Action", align: "center" },
              ].map((column) => (
                <TableCell
                  key={column.label}
                  align={column.align as any}
                  sx={{
                    fontWeight: 600,
                    fontFamily: fontFamily,
                    color: "#474747",
                    fontSize: 14,
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading && rawList.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : paginatedList.length > 0 ? (
              paginatedList.map((item: ITestimonial, index: number) => {
                const reviewerName =
                  item.reviewerName ||
                  item.name ||
                  item.author ||
                  "Reviewer";
                const ratingVal = Number(item.rating ?? 5);
                const commentText =
                  item.comment || item.review || item.message || "";
                const isVerified = Boolean(item.verifiedBuyer);
                const imageUrl =
                  item.image?.url ||
                  (typeof item.image === "string" ? item.image : null) ||
                  item.avatar;
                const dateStr =
                  item.createdAt || item.created_at || item.date || "";

                return (
                  <TableRow
                    key={item.id || index}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleOpenView(item)}
                  >
                    {/* Reviewer Column */}
                    <TableCell align="left">
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          src={imageUrl}
                          alt={reviewerName}
                          sx={{
                            width: 34,
                            height: 34,
                            bgcolor: theme.palette.primary.main,
                            fontSize: 13,
                            fontWeight: 700,
                          }}
                        >
                          {reviewerName.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              color: "#334155",
                              fontSize: 14,
                              fontFamily,
                            }}
                          >
                            {reviewerName}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>

                    {/* Rating Column */}
                    <TableCell align="center">
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        spacing={0.5}
                      >
                        <Rating
                          value={ratingVal}
                          readOnly
                          precision={0.5}
                          size="small"
                          sx={{ color: "#faaf00" }}
                        />
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{ color: "#474747", fontSize: 12 }}
                        >
                          ({ratingVal})
                        </Typography>
                      </Stack>
                    </TableCell>

                    {/* Comment Column */}
                    <TableCell align="left" sx={{ maxWidth: 360 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          color: "#474747",
                          fontSize: 13,
                          lineHeight: 1.5,
                        }}
                      >
                        {commentText || "—"}
                      </Typography>
                    </TableCell>

                    {/* Buyer Status Column */}
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "inline-block",
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          fontWeight: 600,
                          fontSize: 13,
                          backgroundColor: isVerified
                            ? theme.palette.success.lighter ||
                              alpha(theme.palette.success.main, 0.1)
                            : alpha(theme.palette.grey[500], 0.1),
                          color: isVerified
                            ? theme.palette.success.main
                            : "text.secondary",
                        }}
                      >
                        {isVerified ? "Verified" : "Unverified"}
                      </Box>
                    </TableCell>

                    {/* Tag Column */}
                    <TableCell align="center">
                      {item.tag ? (
                        <Chip
                          label={item.tag}
                          size="small"
                          sx={{
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.08
                            ),
                            color: theme.palette.primary.main,
                            fontWeight: 600,
                            fontSize: 12,
                            height: 24,
                          }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>

                    {/* Date Column */}
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        sx={{ color: "#474747", fontSize: 13 }}
                      >
                        {dateStr
                          ? new Date(dateStr).toLocaleDateString("en-IN", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })
                          : "N/A"}
                      </Typography>
                    </TableCell>

                    {/* Action Column */}
                    <TableCell align="center">
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, item)}
                        size="small"
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No reviews found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ----------------- Action Menu (3 Dots) ----------------- */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            minWidth: 150,
            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
            borderRadius: 1,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (selectedRow) handleOpenView(selectedRow);
          }}
        >
          <ListItemText
            primary="View Details"
            primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 500 }}
          />
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (selectedRow) handleOpenEdit(selectedRow);
          }}
        >
          <ListItemText
            primary="Edit"
            primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 500 }}
          />
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (selectedRow) handleOpenDelete(selectedRow);
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemText
            primary={Strings.button.delete}
            primaryTypographyProps={{
              fontSize: "0.85rem",
              fontWeight: 500,
              color: "error.main",
            }}
          />
        </MenuItem>
      </Menu>

      {/* ----------------- Pagination (Matching requests / customers / user) ----------------- */}
      <Stack
        direction="row"
        justifyContent="flex-end"
        mt={2}
        alignItems="center"
        gap={2}
        flexWrap="wrap"
      >
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", fontWeight: 500 }}
        >
          {(() => {
            if (total === 0) return "Showing 0 to 0 of 0 Reviews";
            const start = (page - 1) * rowsPerPage + 1;
            const end = Math.min(page * rowsPerPage, total);
            return `Showing ${start} to ${end} of ${total} Reviews`;
          })()}
        </Typography>

        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, value) => setPage(value)}
          color="primary"
          shape="rounded"
        />

        <TextField
          name="pagination"
          type="number"
          value={page}
          size="small"
          sx={{ width: 60 }}
          onChange={(e) =>
            setPage(() => {
              const val = Number(e.target.value);
              if (val <= totalPages) {
                return val > 0 ? val : 1;
              }
              return 1;
            })
          }
        />
      </Stack>

      {/* ----------------- Add / Edit Modal ----------------- */}
      <AddEditTestimonialModal
        open={openAddEditModal}
        onClose={() => {
          setOpenAddEditModal(false);
          setSelectedItemToEdit(null);
        }}
        selectedTestimonialData={selectedItemToEdit}
      />

      {/* ----------------- Detail View Modal ----------------- */}
      <TestimonialDetailModal
        open={openDetailModal}
        onClose={() => {
          setOpenDetailModal(false);
          setSelectedItemToView(null);
        }}
        testimonialId={selectedItemToView?.id || null}
        initialData={selectedItemToView}
      />

      {/* ----------------- Delete Confirmation Popup ----------------- */}
      <DeleteTestimonialPopup
        open={openDeleteModal}
        onClose={() => {
          setOpenDeleteModal(false);
          setSelectedItemToDelete(null);
        }}
        testimonialId={selectedItemToDelete?.id || null}
        testimonialName={
          selectedItemToDelete?.reviewerName ||
          selectedItemToDelete?.name ||
          selectedItemToDelete?.author
        }
      />
    </Box>
  );
};

export { TestimonialsPage };
